import { render } from "preact"
import { BaseComponent } from "utils/base-component"
import {
  SESSION_RECORDING_EVENTS,
  sendSessionRecordingEvent,
  sendSubscriptionFunnelEvent,
} from "utils/session-recording"
import { Modal } from "./modal"
import { Incentive as IncentiveApp } from "../incentive/Incentive"
import { trackPurchaseForCheckoutSession } from "./services/analytics"
import { Screens } from "./services/screens"

const subscriptionManagementPath = "/profile/subscription"

export const OPEN_PREMIUM_MODAL_EVENT = "surf:open-premium-modal"

// Confirming a checkout return retries transient errors a few times before
// giving up and leaving the retry to a page reload.
const CHECKOUT_PURCHASE_ATTEMPTS = 3
const CHECKOUT_PURCHASE_RETRY_DELAY_MS = 2000

interface CheckoutReturn {
  status: string
  sessionId: string | null
}

function checkoutReturnFromUrl(): CheckoutReturn | null {
  const params = new URLSearchParams(window.location.search)
  const status = params.get("checkout")
  if (!status) return null
  return { status, sessionId: params.get("session_id") }
}

function cleanCheckoutReturnParamsFromUrl(): void {
  const params = new URLSearchParams(window.location.search)
  params.delete("checkout")
  params.delete("session_id")
  const query = params.toString()
  const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`
  window.history.replaceState(window.history.state, "", url)
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

export class SignUp extends BaseComponent {
  static cssClass = "js-signup"

  init() {
    // returning from the Stripe-hosted Checkout page?
    const checkoutReturn = checkoutReturnFromUrl()
    const cancelledCheckoutReturn = checkoutReturn?.status === "cancelled"
    const successCheckoutReturn = checkoutReturn?.status === "success" && checkoutReturn.sessionId
    if (checkoutReturn && !successCheckoutReturn) cleanCheckoutReturnParamsFromUrl()

    // mount the incentive paywall in all .js-incentive elements
    const incentiveEls = document.querySelectorAll(".js-incentive")
    incentiveEls.forEach((el) => {
      el.replaceChildren("")
      this.renderIncentive(el)
    })

    // mount the subscribe paywall in all .js-subscribe elements
    const subscribeEls = document.querySelectorAll(".js-subscribe")
    subscribeEls.forEach((el) => {
      el.replaceChildren("")
      this.renderSubscribe(
        el,
        cancelledCheckoutReturn ? Screens.PaymentScreen : Screens.SignUpScreen,
      )
    })

    // when Checkout was started from the premium modal, reopen it at the
    // payment screen so the visitor can retry
    if (cancelledCheckoutReturn && subscribeEls.length === 0) {
      this.mountModal(Screens.PaymentScreen)
    }

    if (checkoutReturn?.status === "success" && checkoutReturn.sessionId) {
      this.resolveCheckoutSuccessReturn(checkoutReturn.sessionId, subscribeEls)
    }

    // handle premium modal triggers
    const premiumEls = document.querySelectorAll(".js-premium-modal, .js-sign-up-no-free")
    premiumEls.forEach((el) => {
      el.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        if (el.classList.contains("js-sign-up-no-free")) {
          sendSessionRecordingEvent(SESSION_RECORDING_EVENTS.signupNoFreeClick)
        }
        this.openPremiumModal(el)
      })
    })

    document.addEventListener(OPEN_PREMIUM_MODAL_EVENT, (event: Event) => {
      const { paymentProblem = false } =
        (event as CustomEvent<{ paymentProblem?: boolean }>).detail ?? {}
      this.openPremiumModalForPaymentProblem(paymentProblem)
    })

    // handle signup modal triggers
    const signUpEls = document.querySelectorAll(".js-signup-modal")
    signUpEls.forEach((el) => {
      el.addEventListener("click", (event) => {
        event.preventDefault()
        this.mountModal(Screens.SignUpScreen)
      })
    })

    // handle login modal triggers
    const signInEls = document.querySelectorAll(".js-login-modal")
    signInEls.forEach((el) => {
      el.addEventListener("click", (event) => {
        event.preventDefault()
        this.mountModal(Screens.SignInScreen)
      })
    })

    // handle email verify modal
    const emailVerifyEls = document.querySelectorAll(".js-email-verify-modal")
    emailVerifyEls.forEach((el) => {
      el.addEventListener("click", (event) => {
        event.preventDefault()
        this.mountModal(Screens.EmailVerifyScreen)
      })
    })
  }

  // The Stripe-hosted page redirects here even when the payment has not
  // settled yet (delayed payment methods), so the confirmed outcome decides
  // the screen: only a paid session shows success and reports the GA4
  // purchase and Hotjar funnel completion. Until the outcome is terminal the
  // checkout params stay in the URL, so reloading retries the confirmation.
  private async resolveCheckoutSuccessReturn(
    sessionId: string,
    subscribeEls: NodeListOf<Element>,
  ): Promise<void> {
    let outcome = await trackPurchaseForCheckoutSession(sessionId)
    for (
      let attempt = 1;
      outcome === "error" && attempt < CHECKOUT_PURCHASE_ATTEMPTS;
      attempt += 1
    ) {
      // eslint-disable-next-line no-await-in-loop
      await delay(CHECKOUT_PURCHASE_RETRY_DELAY_MS)
      // eslint-disable-next-line no-await-in-loop
      outcome = await trackPurchaseForCheckoutSession(sessionId)
    }

    if (outcome === "recorded" || outcome === "already_recorded") {
      cleanCheckoutReturnParamsFromUrl()
      if (outcome === "recorded") {
        sendSubscriptionFunnelEvent(SESSION_RECORDING_EVENTS.subPaymentSuccess)
      }
      this.showCheckoutReturnScreen(Screens.SuccessScreen, subscribeEls)
    } else {
      this.showCheckoutReturnScreen(Screens.PaymentPendingScreen, subscribeEls)
    }
  }

  private showCheckoutReturnScreen(screen: Screens, subscribeEls: NodeListOf<Element>): void {
    if (subscribeEls.length > 0) {
      subscribeEls.forEach((el) => this.renderSubscribe(el, screen))
    } else {
      this.mountModal(screen)
    }
  }

  private renderIncentive(el: Element): void {
    render(
      <IncentiveApp
        onReload={() => window.location.reload()}
        // Pass a callback so the paywall can open the modal
        onOpenModal={(page = Screens.SignUpScreen) => this.mountModal(page)}
      />,
      el,
    )
  }

  private openPremiumModal(el: Element): void {
    this.openPremiumModalForPaymentProblem((el as HTMLElement).dataset.paymentProblem === "true")
  }

  private openPremiumModalForPaymentProblem(paymentProblem: boolean): void {
    if (paymentProblem) {
      window.location.assign(subscriptionManagementPath)
      return
    }

    this.mountModal(Screens.PaymentScreen)
  }

  // eslint-disable-next-line class-methods-use-this
  private renderSubscribe(el: Element, page = Screens.SignUpScreen): void {
    render(
      <Modal
        action={page}
        source="incentive"
        variant="page"
        onReload={() => window.location.reload()}
      />,
      el,
    )
  }

  private mountModal(page = Screens.SignUpScreen): void {
    render(
      <Modal
        action={page}
        source="incentive"
        onClose={() => this.closeModal()}
        onReload={() => window.location.reload()}
      />,
      this.root,
    )
    document.body.style.setProperty("overflow", "hidden")
    if (this.root !== document.body) {
      document.body.appendChild(this.root)
    }
  }

  private closeModal(): void {
    render(null, this.root)
    document.body.style.removeProperty("overflow")
    if (this.root !== document.body && this.root.parentNode === document.body) {
      document.body.removeChild(this.root)
    }
  }
}
